import { StratusClient } from '@jarvis/web-stratus-client/dist/src/clients/stratus-client';
import { Stack } from '../../../core/types/stratus';
import { getStratusBaseUrl } from '../../stackUtils';
import * as T from './types';
import { internalLogger } from '../../../interface/v1/clientOsLogger';
import getAuthenticationProviderByToken from '../../../utils/tokenUtils/getAuthenticationProviderByToken';
import AuthenticationProviderEnum from '../../../config/authenticationProviderEnum';
import { decodeJWTPayload } from '../../../utils/tokenUtils/JWTUtils';

// Minimal representation of expected decoded token payload fields used here.
interface DecodedTokenPayload {
  stratus_id?: string;
  externalUserRefIDs?: Array<{ type: string; value: string }>;
  [key: string]: unknown;
}

export default class UserMgtClient extends StratusClient {
  apiName = 'UserMgt';
  apiVersion = 'v3';
  usersMeEndpoint = '/users/me';

  public static getBaseUrl(stack: Stack): string {
    return `${getStratusBaseUrl(stack)}/v3/usermgtsvc`;
  }

  public async getUsersMe(options?: {}): Promise<T.UserType> {
    const response = await this.jarvisWebHttpInstance.get({
      url: this.usersMeEndpoint
    });
    return response.data as T.UserType;
  }

  /**
   * Atomic helper to extract the Stratus user resource ID from a decoded token payload
   * based on the authentication provider.
   * Returns null if it cannot be derived.
   */
  private getResourceIdFromToken(
    tokenProvider: AuthenticationProviderEnum,
    payload: DecodedTokenPayload | undefined
  ): string | null {
    switch (tokenProvider) {
      case AuthenticationProviderEnum.authz: {
        const id = payload?.stratus_id || null;
        if (!id) {
          internalLogger?.warn(
            'stratus_id missing in payload for authz provider.'
          );
        }
        return id;
      }
      case AuthenticationProviderEnum.coptor: {
        const externalIds: Array<{ type: string; value: string }> =
          payload?.externalUserRefIDs || [];
        return (
          externalIds.find(
            (id) => id.type === 'EXTERNAL_USER_IDENTIFIER_TYPE_STRATUS'
          )?.value || null
        );
      }
      default:
        internalLogger?.warn(
          `Unsupported tokenProvider=${tokenProvider} when deriving resourceId.`
        );
        return null;
    }
  }

  public async getIdpUserDetails(options: {
    accessToken: string;
  }): Promise<T.IdpUserDetailType | null> {
    try {
      const { accessToken } = options || {};
      if (!accessToken) {
        throw new Error('Error getting access token for IdP user details.');
      }

      const payload = decodeJWTPayload(accessToken);
      if (!payload) {
        throw new Error('Error decoding access token payload.');
      }

      const tokenProvider = getAuthenticationProviderByToken(accessToken);
      if (!tokenProvider) {
        throw new Error('Cannot determine authentication provider from token.');
      }

      const resourceId: string | null = this.getResourceIdFromToken(
        tokenProvider,
        payload
      );
      if (!resourceId) {
        throw new Error('Error extracting resource ID from access token.');
      }

      const response = await this.jarvisWebHttpInstance.get({
        url: `/users/${resourceId}/idpproperties`
      });
      return response.data as T.IdpUserDetailType;
    } catch (error) {
      internalLogger?.error(`Failed to get IdP user details: ${error}`);
      return null;
    }
  }
}
